import {
  CalendarDots,
  FlowArrow,
  ListMagnifyingGlass,
  UsersThree,
} from "@phosphor-icons/react";
import { type ReactNode, useLayoutEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
  DesktopSidebarNavigation,
  PageTitle,
} from "features/layout/components";
import { LeadPreviewSidebar } from "features/leads/components";

import MobileNavigationTabs from "common/components/MobileNavigationTabs";

import { useCampaignContext } from "../../../context/CampaignContext";
import { getFlowTitle, getSortedAndFilteredFlows } from "../../../utils";
import { CampaignInfo } from "../CampaignInfo";
import TargetingPreview from "../TargetingPreview";
import CampaignAudience from "./CampaignAudience";
import CampaignOverview from "./CampaignOverview";
import CampaignSchedule from "./CampaignSchedule";
import ExcludeTargetsPopoverButton from "./ExcludeTargetsPopoverButton";
import FlowDetails from "./FlowDetails";

interface CampaignNavItem {
  label: string;
  path: string;
  icon: ReactNode;
  component: ReactNode;
}

export default function CampaignDetails() {
  const { pathname } = useLocation();
  const { flows, inmail_optimization: isInMailOptimizationEnabled } =
    useCampaignContext();
  const [previewLeadId, setPreviewLeadId] = useState<string>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const sortedAndFilteredFlows = getSortedAndFilteredFlows(
    flows,
    isInMailOptimizationEnabled,
  );

  function openLeadPreview(leadId: string) {
    setPreviewLeadId(leadId);
    setIsPreviewOpen(true);
  }

  const flowNavItems: CampaignNavItem[] = [];

  sortedAndFilteredFlows.forEach((flow) => {
    flowNavItems.push({
      label: getFlowTitle(flow),
      path: `flows/${flow.id}`,
      icon: <FlowArrow weight="fill" />,
      component: (
        <FlowDetails
          flow={flow}
          key={flow.id}
          openLeadPreview={openLeadPreview}
        />
      ),
    });
  });

  const navItems = [
    {
      label: "Overview",
      path: "overview",
      icon: <ListMagnifyingGlass weight="fill" />,
      component: (
        <CampaignOverview
          flows={sortedAndFilteredFlows}
          openLeadPreview={openLeadPreview}
        />
      ),
    },
    {
      label: "Audience",
      path: "audience",
      icon: <UsersThree weight="fill" />,
      component: (
        <CampaignAudience
          openLeadPreview={openLeadPreview}
          isPreviewOpen={isPreviewOpen}
        />
      ),
    },
    ...flowNavItems,
    {
      label: "Schedule",
      path: "schedule",
      icon: <CalendarDots weight="fill" />,
      component: <CampaignSchedule />,
    },
  ];

  let label: string;
  const currentRoute = navItems.find(({ path }) => pathname.includes(path));
  if (currentRoute) {
    label = currentRoute.label;
  }

  useLayoutEffect(() => {
    setIsPreviewOpen(false);
  }, [pathname]);

  return (
    <div className="flex gap-x-2 h-full min-h-0">
      <DesktopSidebarNavigation
        title="Campaign details"
        navItems={navItems}
        backLinkProps={{ to: "../.." }}
      />
      <ContentContainer>
        <ContentHeader>
          <PageTitle>{label}</PageTitle>
        </ContentHeader>
        <ContentScroll className="p-4 gap-4">
          <CampaignInfo />
          <TargetingPreview actionComponent={<ExcludeTargetsPopoverButton />} />
          <MobileNavigationTabs navItems={navItems} className="lg:hidden" />
          <Routes>
            {navItems.map(({ component, path }) => (
              <Route key={path} path={path} element={component} />
            ))}
            <Route
              path="*"
              element={<Navigate to={navItems[0].path} replace />}
            />
          </Routes>
        </ContentScroll>
      </ContentContainer>
      <LeadPreviewSidebar
        initialState={previewLeadId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
}
